// Data catalog for the "Möbel / Einzelne Gegenstände" (furniture) wizard.
// Structure follows the spec: Bereich (room) -> Gegenstände (items) -> optional
// Art (type, single-select) or Größe (size, single-select).
//
// `assemblable: true` marks items that are allowed to appear later in the
// "Benötigen Sie einen Ab- und Aufbauservice?" step (per the spec: we must
// NOT show a sofa or washing machine there, only items that are typically
// disassembled/assembled).

export const SIZE_SML = ["Klein", "Mittel", "Groß"];
export const SIZE_SML_UNKNOWN = [...SIZE_SML, "Nicht bekannt"];

const item = (key, opts = {}) => ({
  key,
  quantityDefault: 0,
  ...opts,
});

export const FURNITURE_AREAS = [
  {
    key: "Wohnzimmer",
    items: [
      item("Sofa", { typeOptions: ["2-Sitzer", "3-Sitzer", "4-Sitzer", "Ecksofa", "Schlafsofa", "Wohnlandschaft"] }),
      item("Sessel"),
      item("Hocker"),
      item("Couchtisch", { sizeOptions: SIZE_SML, assemblable: true }),
      item("Fernseher", { sizeOptions: ["Bis 55 Zoll", "56–75 Zoll", "Über 75 Zoll"] }),
      item("TV-Schrank / Lowboard", { sizeOptions: SIZE_SML, assemblable: true }),
      item("Wohnwand", { sizeOptions: SIZE_SML, assemblable: true }),
      item("Regal", { sizeOptions: SIZE_SML, assemblable: true }),
      item("Bücherregal", { sizeOptions: SIZE_SML, assemblable: true }),
      item("Vitrine", { sizeOptions: SIZE_SML, assemblable: true }),
      item("Kommode", { sizeOptions: SIZE_SML, assemblable: true }),
      item("Sideboard", { sizeOptions: SIZE_SML, assemblable: true }),
      item("Stehlampe"),
      item("Tischlampe"),
      item("Teppich", { sizeOptions: SIZE_SML }),
      item("Bild / Gemälde", { sizeOptions: SIZE_SML }),
      item("Spiegel", { sizeOptions: SIZE_SML }),
      item("Pflanze", { sizeOptions: SIZE_SML }),
      item("Umzugskarton"),
      item("Sonstiges", { requiresDescription: true }),
    ],
  },
  {
    key: "Schlafzimmer",
    items: [
      item("Bett", { typeOptions: ["Einzelbett", "Doppelbett", "Boxspringbett", "Etagenbett", "Futonbett", "Wasserbett"], assemblable: true }),
      item("Bettgestell", { sizeOptions: ["Einzelbett", "Doppelbett"], assemblable: true }),
      item("Matratze", { typeOptions: ["Einzelmatratze", "Doppelmatratze"] }),
      item("Lattenrost", { sizeOptions: ["Einzel", "Doppel"] }),
      item("Kleiderschrank", { sizeOptions: ["1–2 Türen", "3 Türen", "4 Türen", "5 oder mehr Türen", "Schwebetürenschrank"], assemblable: true }),
      item("Nachttisch"),
      item("Kommode", { sizeOptions: SIZE_SML, assemblable: true }),
      item("Schminktisch", { sizeOptions: SIZE_SML }),
      item("Spiegel", { sizeOptions: SIZE_SML }),
      item("Sessel"),
      item("Fernseher", { sizeOptions: ["Bis 55 Zoll", "56–75 Zoll", "Über 75 Zoll"] }),
      item("TV-Schrank"),
      item("Wäschekorb"),
      item("Umzugskarton"),
      item("Sonstiges", { requiresDescription: true }),
    ],
  },
  {
    key: "Kinderzimmer",
    items: [
      item("Babybett", { assemblable: true }),
      item("Kinderbett", { typeOptions: ["Einzelbett", "Hochbett", "Etagenbett"], assemblable: true }),
      item("Matratze"),
      item("Kleiderschrank", { sizeOptions: ["1–2 Türen", "3 Türen", "4 oder mehr Türen"], assemblable: true }),
      item("Wickelkommode"),
      item("Kommode", { assemblable: true }),
      item("Schreibtisch", { sizeOptions: SIZE_SML, assemblable: true }),
      item("Schreibtischstuhl"),
      item("Regal", { sizeOptions: SIZE_SML, assemblable: true }),
      item("Spielzeugkiste"),
      item("Spielküche"),
      item("Kinderstuhl"),
      item("Kinderwagen"),
      item("Umzugskarton"),
      item("Sonstiges", { requiresDescription: true }),
    ],
  },
  {
    key: "Küche",
    items: [
      item("Kühlschrank", { typeOptions: ["Kleiner Kühlschrank", "Standard-Kühlschrank", "Kühl-Gefrierkombination", "Side-by-Side-Kühlschrank"] }),
      item("Gefrierschrank", { sizeOptions: SIZE_SML }),
      item("Gefriertruhe", { sizeOptions: SIZE_SML }),
      item("Waschmaschine"),
      item("Trockner"),
      item("Geschirrspüler", { sizeOptions: ["45 cm", "60 cm"] }),
      item("Herd", { typeOptions: ["Freistehend", "Einbaugerät"] }),
      item("Backofen", { typeOptions: ["Freistehend", "Einbaugerät"] }),
      item("Kochfeld"),
      item("Mikrowelle"),
      item("Dunstabzugshaube"),
      item("Küchenmaschine"),
      item("Kaffeemaschine"),
      item("Küchenschrank", { typeOptions: ["Unterschrank", "Hängeschrank", "Hochschrank"] }),
      item("Küchenzeile", { sizeOptions: ["Bis 2 Meter", "2–4 Meter", "Über 4 Meter", "Nicht bekannt"], assemblable: true }),
      item("Esstisch", { sizeOptions: ["Bis 4 Personen", "4–6 Personen", "Mehr als 6 Personen"], assemblable: true }),
      item("Stuhl"),
      item("Küchenbank", { sizeOptions: SIZE_SML }),
      item("Küchenregal"),
      item("Umzugskarton"),
      item("Sonstiges", { requiresDescription: true }),
    ],
  },
  {
    key: "Esszimmer",
    items: [
      item("Esstisch", { sizeOptions: ["Bis 4 Personen", "4–6 Personen", "Mehr als 6 Personen"], assemblable: true }),
      item("Stuhl"),
      item("Esszimmerbank", { sizeOptions: SIZE_SML }),
      item("Sideboard", { sizeOptions: SIZE_SML, assemblable: true }),
      item("Vitrine", { sizeOptions: SIZE_SML, assemblable: true }),
      item("Kommode", { assemblable: true }),
      item("Regal", { assemblable: true }),
      item("Servierwagen"),
      item("Lampe"),
      item("Sonstiges", { requiresDescription: true }),
    ],
  },
  {
    key: "Arbeitszimmer / Büro",
    items: [
      item("Schreibtisch", { typeOptions: ["Klein", "Mittel", "Groß", "Eckschreibtisch", "Höhenverstellbarer Schreibtisch"], assemblable: true }),
      item("Bürostuhl"),
      item("Besucherstuhl"),
      item("Aktenschrank", { sizeOptions: SIZE_SML, assemblable: true }),
      item("Rollcontainer"),
      item("Regal", { sizeOptions: SIZE_SML, assemblable: true }),
      item("Computer"),
      item("Monitor", { sizeOptions: ["Bis 27 Zoll", "28–40 Zoll", "Über 40 Zoll"] }),
      item("Drucker", { typeOptions: ["Kleiner Drucker", "Großer Drucker / Multifunktionsgerät"] }),
      item("Kopierer", { sizeOptions: ["Klein", "Groß"] }),
      item("Sofa", { typeOptions: ["2-Sitzer", "3-Sitzer", "Schlafsofa"] }),
      item("Umzugskarton"),
      item("Sonstiges", { requiresDescription: true }),
    ],
  },
  {
    key: "Badezimmer",
    items: [
      item("Waschmaschine"),
      item("Trockner"),
      item("Badezimmerschrank", { sizeOptions: SIZE_SML, assemblable: true }),
      item("Spiegelschrank", { sizeOptions: SIZE_SML, assemblable: true }),
      item("Waschbeckenunterschrank", { assemblable: true }),
      item("Regal"),
      item("Wäschekorb"),
      item("Spiegel"),
      item("Sonstiges", { requiresDescription: true }),
    ],
  },
  {
    key: "Flur",
    items: [
      item("Garderobe", { sizeOptions: SIZE_SML, assemblable: true }),
      item("Schuhschrank", { sizeOptions: SIZE_SML, assemblable: true }),
      item("Kommode"),
      item("Spiegel", { sizeOptions: SIZE_SML }),
      item("Bank"),
      item("Kleiderständer"),
      item("Teppich"),
      item("Sonstiges", { requiresDescription: true }),
    ],
  },
  {
    key: "Keller",
    items: [
      item("Regal", { sizeOptions: SIZE_SML, assemblable: true }),
      item("Schwerlastregal", { sizeOptions: SIZE_SML, assemblable: true }),
      item("Schrank"),
      item("Werkzeugschrank"),
      item("Werkbank", { sizeOptions: SIZE_SML, assemblable: true }),
      item("Fahrrad"),
      item("E-Bike"),
      item("Kinderfahrrad"),
      item("Reifen"),
      item("Koffer"),
      item("Getränkekiste"),
      item("Umzugskarton"),
      item("Sonstiges", { requiresDescription: true }),
    ],
  },
  {
    key: "Dachboden",
    items: [
      item("Umzugskarton"),
      item("Koffer"),
      item("Regal"),
      item("Schrank"),
      item("Kommode"),
      item("Tisch"),
      item("Stuhl"),
      item("Weihnachtsdekoration"),
      item("Sonstiges", { requiresDescription: true }),
    ],
  },
  {
    key: "Garage",
    items: [
      item("Fahrrad"),
      item("E-Bike"),
      item("Motorrad"),
      item("Motorroller"),
      item("Reifen"),
      item("Werkzeugschrank"),
      item("Werkbank"),
      item("Regal"),
      item("Schwerlastregal"),
      item("Gartengeräte"),
      item("Rasenmäher", { typeOptions: ["Handrasenmäher", "Elektrischer Rasenmäher", "Benzinrasenmäher", "Aufsitzrasenmäher"] }),
      item("Mülltonne"),
      item("Sonstiges", { requiresDescription: true }),
    ],
  },
  {
    key: "Balkon / Terrasse",
    items: [
      item("Gartentisch", { sizeOptions: SIZE_SML, assemblable: true }),
      item("Gartenstuhl"),
      item("Gartenbank", { sizeOptions: SIZE_SML, assemblable: true }),
      item("Gartenlounge", { sizeOptions: SIZE_SML, assemblable: true }),
      item("Liegestuhl"),
      item("Grill", { typeOptions: ["Kleiner Grill", "Holzkohlegrill", "Gasgrill", "Großer Grill"] }),
      item("Sonnenschirm", { sizeOptions: ["Klein", "Groß"] }),
      item("Sonnenschirmständer"),
      item("Pflanze", { sizeOptions: SIZE_SML }),
      item("Pflanzkübel", { sizeOptions: SIZE_SML }),
      item("Gartenschrank", { sizeOptions: SIZE_SML, assemblable: true }),
      item("Sonstiges", { requiresDescription: true }),
    ],
  },
  {
    key: "Sonstiger Bereich",
    isCustomArea: true, // requires free text "Bitte geben Sie den Bereich an."
    items: [], // custom items added via "Gegenstand hinzufügen" with size Klein/Mittel/Groß/Nicht bekannt
  },
];

export const HEAVY_ITEMS = [
  item("Klavier"),
  item("Flügel"),
  item("E-Piano"),
  item("Tresor", { sizeOptions: ["Bis 100 kg", "101–200 kg", "201–300 kg", "Über 300 kg", "Nicht bekannt"], sizeLabel: "weight" }),
  item("Aquarium", { sizeOptions: ["Bis 100 Liter", "101–300 Liter", "Über 300 Liter", "Nicht bekannt"], warning: "Das Aquarium muss vor dem Transport vollständig entleert sein." }),
  item("Billardtisch", { sizeOptions: ["Klein", "Standard", "Groß", "Nicht bekannt"] }),
  item("Fitnessgerät", { typeOptions: ["Laufband", "Crosstrainer", "Heimtrainer", "Rudergerät", "Kraftstation", "Hantelbank", "Sonstiges"] }),
  item("Massivholzmöbel", { sizeOptions: SIZE_SML }),
  item("Marmor- oder Steintisch", { sizeOptions: SIZE_SML }),
  item("Kunstwerk / großes Bild", { sizeOptions: SIZE_SML }),
  item("Große Pflanze"),
  item("Kopierer"),
  item(
    "Sonstiger schwerer Gegenstand",
    {
      requiresDescription: true,
      sizeOptions: ["Bis 50 kg", "51–100 kg", "101–200 kg", "201–300 kg", "Über 300 kg", "Nicht bekannt"],
      sizeLabel: "weight",
    }
  ),
];

export const ASSEMBLY_SERVICE_OPTIONS = ["Ja", "Nein", "Ich bin nicht sicher"];
export const ASSEMBLY_LEVEL_OPTIONS = ["Nur Abbau", "Nur Aufbau", "Abbau und Aufbau"];

export const KITCHEN_SERVICE_OPTIONS = ["Nur Demontage", "Nur Montage", "Demontage und Montage", "Ich benötige eine Beratung"];
export const KITCHEN_SIZE_OPTIONS = ["Bis 2 Meter", "2–4 Meter", "Über 4 Meter", "Nicht bekannt"];

export const APPLIANCE_CONNECTION_OPTIONS = ["Nein", "Nur abklemmen", "Nur anschließen", "Abklemmen und anschließen", "Ich bin nicht sicher"];
export const APPLIANCE_CONNECTION_ITEMS = ["Waschmaschine", "Geschirrspüler", "Trockner", "Herd / Kochfeld"];